import React, { useState, useMemo, useEffect, useRef } from "react";
import MobileFoodCarousel from "src/components/common/MobileFoodCarousel";
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { foodImageSource } from "../../../../config/foodImage";
import { motion, useReducedMotion } from "framer-motion";

/* IMAGES */
import Achar from "../../../../assets/categories/achar.webp";
import Dessert from "../../../../assets/categories/desert.webp";
import Drinks from "../../../../assets/categories/drinks.webp";
import Khana from "../../../../assets/categories/khana.webp";
import Khaja from "../../../../assets/categories/khaja.webp";
import Snacks from "../../../../assets/categories/snacks.webp";

/* CATEGORY MAP */
const categoryConfig = {
  achar: Achar,
  dessert: Dessert,
  drinks: Drinks,
  khana: Khana,
  khaja: Khaja,
  snacks: Snacks,
};

function CategoryButton({
  categories = [],
  selectedCategory,
  setSelectedCategory,
  type,
}) {
  const theme = useTheme();
  const reduceMotion = useReducedMotion();

  const isMenu = type === "menu";
  const isItems = type === "items";
  const isMainCourses = type === "main";
  const isHorizontalList = isMenu;

  /* RESPONSIVE COUNT */
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const visibleCount = isSmallScreen ? 4 : 4;

  /* PAGINATION STATE */
  const [page, setPage] = useState(0);
  const menuScrollRef = useRef(null);

  useEffect(() => {
    if (!isMenu) return;

    const selectedIndex = categories.findIndex((category) =>
      (typeof category === "string" ? category : category.name) === selectedCategory
    );
    if (selectedIndex < 0) return;

    setPage(Math.floor(selectedIndex / visibleCount));

    if (isSmallScreen) {
      const scroller = menuScrollRef.current;
      const selectedButton = scroller?.querySelector('[aria-pressed="true"]');
      if (selectedButton && typeof scroller.scrollTo === "function") {
        const containerBounds = scroller.getBoundingClientRect();
        const buttonBounds = selectedButton.getBoundingClientRect();
        scroller.scrollTo({
          left: scroller.scrollLeft + buttonBounds.left - containerBounds.left
            - (containerBounds.width - buttonBounds.width) / 2,
          behavior: reduceMotion ? "auto" : "smooth",
        });
      }
    }
  }, [categories, selectedCategory, isMenu, isSmallScreen, visibleCount, reduceMotion]);

  const startIndex = page * visibleCount;
  const visibleCategories = useMemo(() => {
    return isMenu && !isSmallScreen
      ? categories.slice(startIndex, startIndex + visibleCount)
      : categories;
  }, [categories, startIndex, visibleCount, isMenu, isSmallScreen]);

  const maxPage = Math.ceil(categories.length / visibleCount) - 1;
  const hasPagination = isMenu && !isSmallScreen && categories.length > visibleCount;

  const categoryButtons = visibleCategories.map((category) => {
            const item_name = typeof category === "string" ? category : category.name;
            const key = item_name.toLowerCase().trim();
            const fallbackImage = categoryConfig[key] || Snacks;
            const image = typeof category === "object" && category.image
              ? foodImageSource(category.image)
              : categoryConfig[key] || Snacks;
            const isActive = selectedCategory === item_name;

            /* ===================== ITEMS CARD ===================== */
            if (isItems) {
              return (
                <Box
                  key={item_name}
                  component={motion.button}
                  type="button"
                  aria-pressed={selectedCategory === item_name}
                  className={`${isItems ? "course-item-button" : "course-category-button"} ${isItems ? "we-serve-item-button" : ""}`}
                  initial={reduceMotion ? false : { opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, ease: "easeOut", delay: Math.min(categories.indexOf(category) * 0.06, 0.35) }}
                  onClick={() => setSelectedCategory(item_name)}
                  sx={{
                    width: { xs: 156, sm: 182, md: 196 },
                    minHeight: { xs: 218, sm: 236 },
                    cursor: "pointer",
                    flexShrink: 0,
                    borderRadius: "48px 48px 24px 24px",
                    background: "transparent",
                    border: "1px solid transparent",
                    p: { xs: 1.5, sm: 1.75 },
                    textAlign: "center",
                    transition: "transform .25s ease, box-shadow .25s ease",
                    boxShadow: "none",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      background: "linear-gradient(180deg, #f4f8e8 0%, #eaf3d9 100%)",
                      boxShadow: "0 16px 28px rgba(65,70,39,.15)",
                      borderColor: "transparent",
                    },
                  }}
                >
                  {/* IMAGE */}
                  <Box
                    sx={{
                      width: "100%",
                      height: { xs: 112, sm: 126 },
                      // mb: 1,
                      // borderRadius: "12px",
                      // backgroundColor: "#f9fafb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      component="img"
                      src={image}
                      alt={item_name}
                      loading="lazy"
                      decoding="async"
                      width="120"
                      height="120"
                      sx={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        borderRadius: "50%",
                        boxShadow: "0 6px 14px rgba(65,70,39,.14)",
                      }}
                    />
                  </Box>

                  {/* NAME */}
                  <Typography
                    sx={{
                      fontSize: { xs: "0.92rem", sm: "1rem" },
                      fontWeight: 700,
                      color: "#252920",
                      textTransform: "capitalize",
                      lineHeight: 1.2,
                    }}
                  >
                    {item_name}
                  </Typography>

                  {/* SUBTEXT */}
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      color: "#747767",
                      mt: 0.3,
                      textTransform: "capitalize",
                    }}
                  >
                    Enjoy {item_name}
                  </Typography>

                  {/* CTA */}
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      my: 1,
                      color: "#b58519",
                    }}
                  >
                    View Menu →
                  </Typography>
                </Box>
              );
            }

            /* ===================== MENU / CATEGORY BUTTON ===================== */
            return (
              <Box
                key={item_name}
                component={motion.button}
                  type="button"
                  aria-pressed={selectedCategory === item_name}
                  className={`${isItems ? "course-item-button" : "course-category-button"} ${isMainCourses ? "we-serve-category-button" : ""}`}
                initial={reduceMotion ? false : { opacity: 0, y: isMainCourses ? 24 : 18, scale: isMainCourses ? 0.97 : 0.94 }}
                animate={isMainCourses ? undefined : { opacity: 1, y: 0, scale: 1 }}
                whileInView={isMainCourses ? { opacity: 1, y: 0, scale: 1 } : undefined}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: reduceMotion ? 0 : isMainCourses ? 0.7 : 0.5, ease: [0.22, 1, 0.36, 1], delay: reduceMotion ? 0 : Math.min(categories.indexOf(category) * 0.1, 0.4) }}
                onClick={() => setSelectedCategory(item_name)}
                sx={{
                  width: isMenu ? { xs: 100, sm: 116, md: 128, lg: 140 } : { xs: 152, sm: 172, md: 144, lg: 132 },
                  minWidth: isMenu ? 0 : { xs: 152, sm: 172, md: 144, lg: 132 },
                  maxWidth: isMenu ? "100%" : undefined,
                  flexShrink: 0,
                  scrollSnapAlign: "center",
                  minHeight: "auto",
                  padding: { xs: 0.5, sm: 1 },
                  borderRadius: 0,
                  background: "transparent",
                  border: 0,
                  boxShadow: "none",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  transition: "box-shadow 0.3s ease",
                  "&:hover": isMainCourses ? {} : { transform: "translateY(-5px)" },
                }}
              >
                <Box
                  sx={{
                    width: isMenu ? { xs: "clamp(64px, 22vw, 88px)", sm: 100, md: 112, lg: 124 } : { xs: 140, sm: 160, md: 132, lg: 120 },
                    height: isMenu ? "auto" : { xs: 140, sm: 160, md: 132, lg: 120 },
                    maxWidth: "100%",
                    aspectRatio: "1 / 1",
                    borderRadius: "50%",
                    backgroundColor: "#eee9dc",
                    border: 0,
                    boxShadow: isActive
                      ? "0 0 0 2px rgba(226, 173, 48, 0.65), 0 8px 18px rgba(65, 70, 39, 0.12)"
                      : "0 5px 14px rgba(65, 70, 39, 0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src={image}
                    alt={item_name}
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = fallbackImage;
                    }}
                    width="100"
                    height="100"
                    loading="lazy"
                    decoding="async"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      borderRadius: "50%",
                      mixBlendMode: "multiply",
                      transform: "scale(1.28)",
                      filter: "saturate(1.18) contrast(1.05) drop-shadow(0 7px 12px rgba(65,70,39,.18))",
                    }}
                  />
                </Box>

                <Typography
                  sx={{
                    mt: { xs: 0.75, sm: 1 },
                    fontSize: isMenu ? { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" } : { xs: "0.75rem", sm: "0.86rem" },
                    fontWeight: 700,
                    color: "#252920",
                    textTransform: "capitalize",
                    whiteSpace: isMenu ? "normal" : "nowrap",
                    overflowWrap: isMenu ? "anywhere" : undefined,
                    fontFamily: "Roboto Serif, Georgia, serif",
                  }}
                >
                  {item_name}
                </Typography>
              </Box>
            );
          });

  if (isMainCourses) {
    return (
      <Box
        className="we-serve-category-scroll"
        role="region"
        aria-label="Main course categories"
        tabIndex={0}
        sx={{
          width: "100%", minWidth: 0, overflowX: "auto",
          scrollbarWidth: "thin", scrollbarColor: "#b58519 #eee9dc",
          scrollSnapType: "x proximity",
          "&::-webkit-scrollbar": { height: 8 },
          "&::-webkit-scrollbar-track": { background: "#eee9dc", borderRadius: 4 },
          "&::-webkit-scrollbar-thumb": { background: "#b58519", borderRadius: 4 },
          "&:focus-visible": { outline: "2px solid #b58519", outlineOffset: -2 },
        }}
      >
        <Box className="we-serve-category-row" sx={{
          display: "flex", flexDirection: "row", flexWrap: "nowrap",
          alignItems: "flex-start", justifyContent: "center",
          width: "max-content", minWidth: "100%", boxSizing: "border-box",
          gap: { xs: 1.5, sm: 2.5 }, px: 1, pt: 2, pb: 3,
        }}>
          {categoryButtons}
        </Box>
      </Box>
    );
  }

  if (isMenu && isSmallScreen) {
    return (
      <Box ref={menuScrollRef} sx={{ width: "100%", minWidth: 0, overflowX: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" }, scrollSnapType: "x proximity" }}>
        <Box sx={{ display: "flex", width: "max-content", minWidth: "100%", justifyContent: "center", gap: "clamp(8px, 2vw, 16px)", px: 1, py: 1.5 }}>
          {categoryButtons}
        </Box>
      </Box>
    );
  }

  if (isSmallScreen) {
    return (
      <MobileFoodCarousel
        className={isMainCourses ? "we-serve-mobile-slider" : undefined}
        breakpoint={isMainCourses ? "md" : "sm"}
        variableWidth={isMenu}
        gap={16}
        slidesToShow={isMainCourses && !isSmallScreen ? 3 : isItems ? 1 : 2}
        cardWidth={isMainCourses && !isSmallScreen ? 200 : isItems ? 240 : isMenu ? 100 : 172}
      >
        {categoryButtons}
      </MobileFoodCarousel>
    );
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%", minWidth: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1,
          width: "100%",
          minWidth: 0,
          maxWidth: 1120,
        }}
      >
        {/* PREVIOUS BUTTON */}
        {hasPagination && (
          <IconButton
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
        )}

        {/* CATEGORY LIST */}
        <Box
          sx={{
            width: "100%",
            minWidth: 0,
            mt: 1,
            overflowX: { xs: isHorizontalList ? "auto" : "visible", md: "visible" },
            overflowY: { xs: isHorizontalList ? "hidden" : "visible", md: "visible" },
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            scrollSnapType: { xs: isHorizontalList ? "x proximity" : "none", md: "none" },
            touchAction: { xs: isHorizontalList ? "pan-x pan-y" : "auto", md: "auto" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: { xs: isHorizontalList ? "nowrap" : "wrap", md: "wrap" },
              justifyContent: "center",
              alignItems: "flex-start",
              gap: isMenu ? 2 : { xs: 1.5, sm: 2.5 },
              rowGap: isMenu ? 2 : { xs: 2, sm: 2.5 },
              width: { xs: isHorizontalList ? "max-content" : "100%", md: "100%" },
              minWidth: "100%",
              px: { xs: 1, sm: 2 },
              pb: 1,
            }}
          >
          {categoryButtons}
          </Box>
        </Box>

        {/* NEXT BUTTON */}
        {hasPagination && (
          <IconButton
            disabled={page === maxPage}
            onClick={() => setPage((p) => p + 1)}
            // sx={{ mr: { xs: 0, sm: 2, md: 5 } }}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}

export default CategoryButton;
